import { ChangeDetectionStrategy, Component, DestroyRef, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { SyllabusQuery } from '@examdojo/category/v2';
import { ButtonComponent } from '@examdojo/core/button';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { isNullish } from '@examdojo/core/util/nullish';
import { SourceService } from '@examdojo/source';
import { ToastService } from '@examdojo/toast';
import { TextInputComponent } from '@examdojo/ui/input';
import { EMPTY, finalize, tap } from 'rxjs';
import { SourceCreateDialogService } from './source-create-dialog.service';

@Component({
  selector: 'exam-source-create-dialog',
  template: `
    <div>
      <dojo-text-input label="Name" [formCtrl]="name" />
      <dojo-button (click)="create()" [pending]="isCreating()">Create</dojo-button>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, TextInputComponent, ButtonComponent],
  host: {
    class: 'overflow-y-auto',
  },
})
export class SourceCreateDialogComponent {
  constructor(
    private readonly sourceCreateDialogService: SourceCreateDialogService,
    private readonly sourceService: SourceService,
    private readonly syllabusQuery: SyllabusQuery,
    private readonly toastService: ToastService,
    private readonly destroyRef: DestroyRef,
    private readonly errorHandlingService: ErrorHandlerService,
  ) {}

  readonly isCreating = signal(false);

  readonly name = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required],
  });

  create() {
    if (!this.name.valid) {
      return;
    }

    this.isCreating.set(true);

    const syllabusId = this.syllabusQuery.getActiveId();

    if (isNullish(syllabusId)) {
      this.toastService.error('No syllabus selected');
      return;
    }

    this.sourceService
      .create({ name: this.name.value, syllabus_id: syllabusId })
      .pipe(
        this.errorHandlingService.catchHttpErrors(() => EMPTY, {
          showErrorDetailsInToast: true,
        }),
        tap((source) => {
          this.sourceCreateDialogService.dismiss(source.id);
        }),
        finalize(() => this.isCreating.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
